import { ORIGENS_DE_CANCELAMENTO, ROTULO_DA_ORIGEM_DE_CANCELAMENTO } from '@locaveri/shared'
import { Plus, Undo2, Trash2 } from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import {
  useAtualizarMotivo,
  useCriarMotivo,
  useMotivosDeCancelamento,
  type MotivoDaApi,
} from '@/features/pesquisa/api'
import { Skeleton } from '@/components/ui/skeleton'
import { errorMessage } from '@/lib/api'

/**
 * MOTIVOS DE CANCELAMENTO — o catálogo que substitui o texto livre.
 *
 * ===========================================================================
 * POR QUE UM CATÁLOGO, E NÃO UM CAMPO DE TEXTO
 * ===========================================================================
 * "achou caro", "carro caro", "preço" e "tava caro" são a MESMA resposta e
 * quatro linhas distintas, e ninguém agrupa isso à mão três meses depois. O
 * texto livre continua ao lado no cancelamento — nenhuma lista cobre tudo, e o
 * que não coube nela é o que vira item novo aqui.
 *
 * ===========================================================================
 * ⚠️ A ORIGEM NÃO É COSMÉTICA
 * ===========================================================================
 * `CLIENTE`, `EMPRESA` e `AMBOS` decidem em QUAL lista o motivo aparece, e a API
 * recusa registrar um motivo da empresa num cancelamento do cliente. Sem essa
 * separação, o atendente escolheria "sem veículo disponível" para um
 * cancelamento que foi do cliente, e o relatório de "por que perdemos locações"
 * apontaria para o lado errado — sem nunca errar visivelmente.
 *
 * ===========================================================================
 * NÃO EXISTE EXCLUIR
 * ===========================================================================
 * As transições de reserva apontam para o motivo. Apagar um apagaria a razão de
 * cancelamentos já registrados; por isso a única saída é DESATIVAR, e o item
 * desligado continua visível para poder voltar.
 */

export function MotivosDeCancelamento({
  podeEditar,
}: {
  readonly podeEditar: boolean
}): ReactElement {
  const consulta = useMotivosDeCancelamento({ incluirInativos: true })
  const criar = useCriarMotivo()
  const atualizar = useAtualizarMotivo()

  const [rotulo, setRotulo] = useState('')
  const [origem, setOrigem] = useState<(typeof ORIGENS_DE_CANCELAMENTO)[number]>('CLIENTE')

  const itens = consulta.data?.itens ?? []

  function acrescentar(): void {
    const texto = rotulo.trim()
    if (texto.length < 3) {
      toast.error('O motivo precisa de um rótulo com pelo menos 3 letras.')
      return
    }

    criar.mutate(
      { rotulo: texto, origem, ordem: itens.length, ativo: true },
      {
        onSuccess: () => {
          setRotulo('')
          toast.success('Motivo acrescentado.')
        },
        onError: (erro) => {
          toast.error(errorMessage(erro))
        },
      },
    )
  }

  function alternar(motivo: MotivoDaApi): void {
    atualizar.mutate(
      {
        id: motivo.id,
        dados: {
          rotulo: motivo.rotulo,
          origem: motivo.origem,
          ordem: motivo.ordem,
          ativo: !motivo.ativo,
        },
      },
      {
        onError: (erro) => {
          toast.error(errorMessage(erro))
        },
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Motivos de cancelamento</CardTitle>
        <CardDescription>
          A lista que aparece na hora de cancelar. O campo de texto livre continua ao lado — o que
          não couber aqui é o que vira item novo.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {/* ⚠️ OS TRÊS ESTADOS SÃO DISTINTOS, e confundi-los foi o defeito que
            estava aqui: `itens` era `data?.itens ?? []`, então CARREGANDO e
            FALHOU renderizavam a mesma frase de "nenhum motivo cadastrado".
            O gestor via um catálogo vazio, criava de novo os nove motivos que
            já existiam, e a lista dobrava — com a série histórica partida entre
            os antigos e os novos. A Definition of Done pede os três estados em
            toda tela justamente por isso. */}
        {consulta.isPending && <Skeleton className="h-24 w-full" />}

        {consulta.isError && (
          <Alert variant="danger">
            <AlertTitle>Não foi possível carregar os motivos</AlertTitle>
            <AlertDescription>
              {errorMessage(consulta.error)} A lista abaixo NÃO está vazia — ela não carregou.
              Recarregue antes de cadastrar qualquer coisa.
            </AlertDescription>
          </Alert>
        )}

        {!consulta.isPending && !consulta.isError && itens.length === 0 && (
          <p className="text-sm text-fg-muted">
            Nenhum motivo cadastrado. Sem catálogo, o cancelamento cai no texto livre — e quatro
            jeitos de escrever a mesma coisa viram quatro linhas que ninguém soma depois.
          </p>
        )}

        <ul className="flex flex-col gap-2">
          {itens.map((motivo) => (
            <li
              key={motivo.id}
              className={`flex items-center gap-3 rounded-[10px] border border-border px-3 py-2 ${
                motivo.ativo ? '' : 'opacity-50'
              }`}
            >
              <span className="flex-1 text-sm">{motivo.rotulo}</span>

              <Badge variant="outline">{ROTULO_DA_ORIGEM_DE_CANCELAMENTO[motivo.origem]}</Badge>

              {motivo.usos > 0 && (
                <Badge
                  variant="neutral"
                  title="Este motivo já foi usado em cancelamentos. Desativar tira da lista nova; o histórico continua inteiro."
                >
                  {motivo.usos} uso{motivo.usos === 1 ? '' : 's'}
                </Badge>
              )}

              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={!podeEditar || atualizar.isPending}
                aria-label={
                  motivo.ativo ? `Desativar ${motivo.rotulo}` : `Reativar ${motivo.rotulo}`
                }
                title={
                  motivo.ativo
                    ? 'Desativar. Ele some da lista de escolha; os cancelamentos já registrados continuam apontando para ele.'
                    : 'Reativar'
                }
                onClick={() => {
                  alternar(motivo)
                }}
              >
                {motivo.ativo ? <Trash2 className="size-4" /> : <Undo2 className="size-4" />}
              </Button>
            </li>
          ))}
        </ul>

        {podeEditar && !consulta.isPending && !consulta.isError && (
          <div className="flex flex-wrap items-end gap-2 border-t border-border pt-4">
            <div className="flex min-w-48 flex-1 flex-col gap-2">
              <Label htmlFor="novo-motivo">Novo motivo</Label>
              <Input
                id="novo-motivo"
                value={rotulo}
                placeholder="Ex.: Encontrou preço melhor"
                onChange={(evento) => {
                  setRotulo(evento.target.value)
                }}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="origem-do-motivo">Quem escolhe</Label>
              <Select
                id="origem-do-motivo"
                className="w-56"
                value={origem}
                onChange={(evento) => {
                  setOrigem(evento.target.value as (typeof ORIGENS_DE_CANCELAMENTO)[number])
                }}
              >
                {ORIGENS_DE_CANCELAMENTO.map((opcao) => (
                  <option key={opcao} value={opcao}>
                    {ROTULO_DA_ORIGEM_DE_CANCELAMENTO[opcao]}
                  </option>
                ))}
              </Select>
            </div>

            <Button type="button" disabled={criar.isPending} onClick={acrescentar}>
              <Plus className="mr-2 size-4" />
              Acrescentar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
